import { useMutation } from '@apollo/client/react'
import { Button, Popconfirm, Space, Tooltip, useMessage } from '@faceup/ui-base'
import { DELETE_REPORTS_INTERVAL_DEFAULT_MONTHS } from '@faceup/utils'
import { useState } from 'react'
import { graphql } from '../__generated__'

type ResetDeleteReportsIntervalButtonProps = {
  motherId: string
  isAlreadySet: boolean
}

const mutations = {
  setDeleteReportsInterval: graphql(`
    mutation setDeleteReportsIntervalMutation($input: SetDeleteReportsIntervalInput!) {
      setDeleteReportsInterval(input: $input) {
        success
      }
    }
  `),
  resetDeleteReportsInterval: graphql(`
    mutation resetDeleteReportsIntervalMutation($input: ResetDeleteReportsIntervalInput!) {
      resetDeleteReportsInterval(input: $input) {
        success
      }
    }
  `),
}

export const ResetDeleteReportsIntervalButton = ({
  motherId,
  isAlreadySet,
}: ResetDeleteReportsIntervalButtonProps) => {
  const message = useMessage()
  const [localIsSet, setLocalIsSet] = useState(isAlreadySet)

  const [setDeleteReportsInterval, { loading: setLoading }] = useMutation(
    mutations.setDeleteReportsInterval,
    {
      onError: () => {
        message.error('Failed to set delete reports interval')
      },
      onCompleted: data => {
        if (data.setDeleteReportsInterval?.success) {
          setLocalIsSet(true)
          return message.success('Delete reports interval set to 0 days successfully')
        }
        return message.error('Failed to set delete reports interval')
      },
    }
  )

  const [resetDeleteReportsInterval, { loading: resetLoading }] = useMutation(
    mutations.resetDeleteReportsInterval,
    {
      onError: () => {
        message.error('Failed to reset delete reports interval')
      },
      onCompleted: data => {
        if (data.resetDeleteReportsInterval?.success) {
          setLocalIsSet(false)
          return message.success(
            `Delete reports interval reset to ${DELETE_REPORTS_INTERVAL_DEFAULT_MONTHS} months successfully`
          )
        }
        return message.error('Failed to reset delete reports interval')
      },
    }
  )

  // Replaced imperative modal.confirm usage with Popconfirm wrappers in JSX

  return (
    <Space>
      <Tooltip title={localIsSet ? 'Deletion interval is already set to 0 days' : undefined}>
        <Popconfirm
          title={
            'Set minimum lifespan for case deletion to 0 days. This will enable case deletion and set the minimum lifespan to 0 days. Are you sure?'
          }
          onConfirm={() =>
            setDeleteReportsInterval({
              variables: {
                input: {
                  motherId,
                },
              },
            })
          }
        >
          <Button ghost type='primary' loading={setLoading} disabled={localIsSet}>
            Set deletion interval to 0 days
          </Button>
        </Popconfirm>
      </Tooltip>
      {localIsSet && (
        <Popconfirm
          title={`Reset case deletion interval to ${DELETE_REPORTS_INTERVAL_DEFAULT_MONTHS} months. This will disable case deletion and reset the minimum lifespan to ${DELETE_REPORTS_INTERVAL_DEFAULT_MONTHS} months. Are you sure?`}
          onConfirm={() =>
            resetDeleteReportsInterval({
              variables: {
                input: {
                  motherId,
                },
              },
            })
          }
        >
          <Button ghost type='primary' danger loading={resetLoading}>
            Reset deletion interval to {DELETE_REPORTS_INTERVAL_DEFAULT_MONTHS} months
          </Button>
        </Popconfirm>
      )}
    </Space>
  )
}
